import {Component, Input, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {BanDto, BanWordDto, Level, StandardCodeDto} from '../../../../generated-model/models';
import {formatRecordedAt, resolveRecordedBy} from '../../../shared/commons/audit-display.util';

type BanUpsertPayload = Omit<Partial<BanDto>, 'banWords'> & { banWords?: Array<Partial<BanWordDto>> };

@Component({
  selector: 'app-ban-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './ban-modal.html',
  styleUrl: './ban.scss'
})
export class BanModal implements OnInit {
  @Input() ban: BanUpsertPayload | undefined;
  @Input() standardCodes: StandardCodeDto[] = [];
  @Input() viewOnly = false;
  @Input() onSave?: (payload: any, isEditMode: boolean) => Promise<boolean>;

  form: FormGroup;
  submitted = false;
  isSubmitting = false;
  isEditMode = false;
  recordedByDisplay = '-';
  recordedAtDisplay = '-';
  readonly majorLevel: Level.major = Level.major;
  readonly minorLevel: Level.minor = Level.minor;
  private nextRowKey = 1;

  constructor(public activeModal: NgbActiveModal, private fb: FormBuilder) {
    this.form = this.fb.group({
      id: [null],
      standardCodeId: [null, [Validators.required]],
      ordering: [0],
      state: [1],
      majorWordInput: [''],
      minorWordInput: [''],
      majorWords: this.fb.array([]),
      minorWords: this.fb.array([])
    });
  }

  ngOnInit(): void {
    if (!this.ban) {
      this.form.reset({
        id: null,
        standardCodeId: null,
        ordering: 0,
        state: 1,
        majorWordInput: '',
        minorWordInput: ''
      });
      this.recordedByDisplay = '-';
      this.recordedAtDisplay = '-';
      return;
    }

    this.isEditMode = true;
    this.recordedByDisplay = resolveRecordedBy(this.ban);
    this.recordedAtDisplay = formatRecordedAt(this.ban);
    this.form.patchValue({
      id: this.ban.id ?? null,
      standardCodeId: this.ban.standardCodeId ?? null,
      ordering: this.ban.ordering ?? 0,
      state: this.ban.state ?? 1,
      majorWordInput: '',
      minorWordInput: ''
    });

    (this.ban.banWords ?? []).forEach((word) => {
      if (word.level === Level.major) {
        this.majorWords.push(this.createWordGroup(word, false));
      } else if (word.level === Level.minor) {
        this.minorWords.push(this.createWordGroup(word, false));
      }
    });
  }

  get selectedStandardCodeLabel(): string {
    const selectedId = this.form.get('standardCodeId');
    if (!selectedId) {
      return '-';
    }
    const item = this.standardCodes.find((code) => code.id === selectedId);
    if (!item) {
      return String(selectedId);
    }
    const preCode = item.preCode?.trim() ?? '';
    const standardCode = item.standardCode?.trim() ?? '';
    const title = item.title?.trim() ?? '';
    return `${preCode} ${standardCode} ${title}`.trim();
  }

  get majorWords(): FormArray {
    return this.form.get('majorWords') as FormArray;
  }

  get minorWords(): FormArray {
    return this.form.get('minorWords') as FormArray;
  }

  addWord(level: Level.major | Level.minor): void {
    if (this.viewOnly) {
      return;
    }
    const controlName = level === Level.major ? 'majorWordInput' : 'minorWordInput';
    const inputControl = this.form.get(controlName);
    const title = String(inputControl?.value ?? '').trim();
    if (!title) {
      return;
    }

    const target = level === Level.major ? this.majorWords : this.minorWords;
    target.insert(0, this.createWordGroup({title, level, channel: 'web'}, true));
    inputControl?.setValue('');
  }

  removeWord(level: Level.major | Level.minor, index: number): void {
    if (this.viewOnly) {
      return;
    }
    const target = level === Level.major ? this.majorWords : this.minorWords;
    target.removeAt(index);
  }

  save(): void {
    if (this.viewOnly) {
      return;
    }
    this.submitted = true;
    if (this.form.invalid || this.hasEmptyWordTitle()) {
      return;
    }

    const majorWords = this.majorWords.controls.map((control, index) => this.toWordDto(control as FormGroup, Level.major, index));
    const minorWords = this.minorWords.controls.map((control, index) => this.toWordDto(control as FormGroup, Level.minor, index));
    const payload: BanUpsertPayload = {
      id: this.toNumber(this.form.get('id')?.value),
      standardCodeId: this.form.get('standardCodeId'),
      ordering: this.toNumber(this.form.get('ordering')?.value) ?? 0,
      state: this.toNumber(this.form.get('state')?.value) ?? 1,
      banWords: [...majorWords, ...minorWords]
    };

    void this.submitAndClose(payload);
  }

  private createWordGroup(word: Partial<BanWordDto>, isNew: boolean): FormGroup {
    return this.fb.group({
      rowKey: [this.nextRowKey++],
      id: [word.id ?? null],
      title: [word.title ?? '', [Validators.required, Validators.maxLength(255)]],
      channel: [word.channel ?? 'web'],
      isNew: [isNew]
    });
  }

  private toWordDto(form: FormGroup, level: Level.major | Level.minor, index: number): Partial<BanWordDto> {
    const idValue = this.toNumber(form.get('id')?.value);
    const banId = this.toNumber(this.form.get('id')?.value);
    const channel = String(form.get('channel')?.value ?? '').trim();

    return {
      id: idValue,
      banId,
      title: String(form.get('title')?.value ?? '').trim(),
      level,
      ordering: index + 1,
      channel: channel || 'web'
    };
  }

  private hasEmptyWordTitle(): boolean {
    const arrays = [this.majorWords, this.minorWords];
    for (const array of arrays) {
      for (const control of array.controls) {
        const title = String((control as FormGroup).get('title')?.value ?? '').trim();
        if (!title) {
          return true;
        }
      }
    }
    return false;
  }

  private toNumber(value: unknown): number | undefined {
    if (value === null || value === undefined || value === '') {
      return undefined;
    }
    return Number(value);
  }

  private async submitAndClose(payload: any): Promise<void> {
    if (!this.onSave) {
      this.activeModal.close(payload);
      return;
    }

    if (this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    try {
      const isSuccess = await this.onSave(payload, this.isEditMode);
      if (isSuccess) {
        this.activeModal.close(payload);
      }
    } finally {
      this.isSubmitting = false;
    }
  }
}
